import React from "react";
import * as Yup from "yup";
import { useQuery } from "@apollo/client/react";
import sortBy from "lodash/sortBy";
import { Formik } from "formik";
import useMutation from "../../hooks/useMutation";
import useAuth from "../../hooks/useAuth";
import SchedulerDialogForm from "./SchedulerDialogForm";
import {
  CREATE_SCHEDULER_MUTATION,
  UPDATE_SCHEDULER_MUTATION,
} from "../../constants/graphql/mutations";
import { PROJECTS_NAMES_QUERY } from "../../constants/graphql/queries";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  projectId: Yup.string().required("Required"),
  frequency: Yup.string().required("Required"),
  daysOfWeek: Yup.string().when("frequency", {
    is: "weekly",
    then: schema => schema.required("Day of the Week is required for weekly frequency"),
    otherwise: schema => schema,
  }),
  dayOfMonth: Yup.string().when("frequency", {
    is: "monthly",
    then: schema => schema.required("Day of the Month is required for monthly frequency"),
    otherwise: schema => schema,
  }),
});

export default function AddSchedulerDialog({
  onClose,
  onSchedulerAdded,
  onDelete,
  entity,
  scheduler = null,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const isEditing = !!scheduler;

  const { data: { projects = [] } = {}, loading: projectsLoading } = useQuery(PROJECTS_NAMES_QUERY);

  const [createScheduler, { loading: createLoading }] = useMutation(CREATE_SCHEDULER_MUTATION, {
    onSuccess: data => {
      onSchedulerAdded(data.scheduler);
    },
    onFailure: errors => {
      console.error("Error creating scheduler:", errors);
    },
  });

  const [updateScheduler, { loading: updateLoading }] = useMutation(UPDATE_SCHEDULER_MUTATION, {
    onSuccess: data => {
      onSchedulerAdded(data.scheduler);
    },
    onFailure: errors => {
      console.error("Error updating scheduler:", errors);
    },
  });

  const mutationLoading = createLoading || updateLoading;

  const projectList = sortBy(
    projects.filter(project => project.active),
    "name"
  );

  const projectsOptions = projectList.map(project => ({
    label: project.name,
    value: project.id,
  }));

  const getInitialValues = () => {
    if (isEditing && scheduler) {
      const projectId = scheduler.reportsSchedulers?.[0]?.project?.id || "";

      return {
        projectId,
        name: scheduler.name || "",
        frequency: scheduler.frequency || "",
        daysOfWeek: Array.isArray(scheduler.daysOfWeek)
          ? scheduler.daysOfWeek[0] || ""
          : scheduler.daysOfWeek || "",
        dayOfMonth: scheduler.dayOfMonth || "",
        dateRangeMode: scheduler.dateRangeMode || "all_time",
        notes: scheduler.notes || "",
        options: scheduler.reportsSchedulers?.[0]?.options,
      };
    }

    return {
      projectId: "",
      name: "",
      frequency: "",
      daysOfWeek: "",
      dayOfMonth: "",
      dateRangeMode: "all_time",
      notes: "",
    };
  };

  const initialValues = getInitialValues();

  const handleSubmit = async values => {
    if (isEditing && scheduler) {
      const oldProjectId = scheduler.reportsSchedulers?.[0]?.project?.id;
      const projectChanged = oldProjectId && oldProjectId !== values.projectId;

      const updateVariables = {
        id: scheduler.id,
        name: values.name || null,
        frequency: values.frequency,
        daysOfWeek: values.frequency === "weekly" ? [values.daysOfWeek] : null,
        dayOfMonth: values.frequency === "monthly" ? values.dayOfMonth : null,
        dateRangeMode: values.dateRangeMode || "all_time",
        notes: values.notes || null,
        addReports: entity
          ? [{ projectId: values.projectId, favoriteReportId: entity.id, options: values.options }]
          : [],
        removeReports: projectChanged && entity
          ? [{ projectId: oldProjectId, favoriteReportId: entity.id }]
          : [],
      };

      await updateScheduler({ variables: updateVariables });
    } else {
      await createScheduler({
        variables: {
          name: values.name || null,
          frequency: values.frequency,
          daysOfWeek: values.frequency === "weekly" ? [values.daysOfWeek] : null,
          dayOfMonth: values.frequency === "monthly" ? values.dayOfMonth : null,
          dateRangeMode: values.dateRangeMode || "all_time",
          notes: values.notes || null,
          projectsReports: entity
            ? [{ projectId: values.projectId, favoriteReportId: entity.id, options: values.options }]
            : [],
        },
      });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <SchedulerDialogForm
        onClose={onClose}
        onDelete={onDelete}
        mutationLoading={mutationLoading}
        projectsLoading={projectsLoading}
        projectsOptions={projectsOptions}
        isEditing={isEditing}
        settings={settings}
        entity={entity}
      />
    </Formik>
  );
}
